import type { QueryRowType } from "@rocicorp/zero";
import { useQuery, useZero } from "@rocicorp/zero/react";
import { useEffect, useRef, useState, type ComponentProps } from "react";
import { sharedMutators } from "../../../shared/mutators";
import { queries } from "../../../shared/queries";
import type { ShareableScopeKind } from "../../../shared/rbac/permissions";
import {
  type GrantEntry,
  SCOPE_MANAGE_GRANTS_PERMISSION,
} from "../../../shared/rbac/roles";
import {
  useAffectedChatCount,
  useConnectorDisconnectImpact,
} from "../../hooks/useConnectorDisconnectImpact";
import { useGrantSpaceAccess } from "../../hooks/useGrantSpaceAccess";
import { useGroupMemberships } from "../../hooks/useGroupMemberships";
import { useHasAccess } from "../../hooks/useHasAccess";
import { useCurrentUserGrants } from "../../hooks/useCurrentUserGrants";
import { useCurrentUserId } from "../../hooks/useCurrentUserId";
import { connectorIdsBySpace } from "../../../shared/liveConnectorUsage";
import { Text } from "../Text";
import { getWritableScopeIds } from "../../lib/getWritableScopeIds";
import { computeSpaceMemberSupplierGap } from "../../lib/spaceMemberSupplierGap";
import { deriveScopeGrantDelta } from "../../lib/spaceGrantDelta";
import { usedSkillsForSpace } from "../../lib/usedSuppliersForSpace";
import { grantEntriesToRows } from "../../lib/grantAssembly";
import { ConfirmModal } from "../ConfirmModal";
import { Count, DisconnectWarning } from "../DisconnectWarning";
import { ResourceAccessTable } from "./ResourceAccessTable";

type Props = Pick<
  ComponentProps<typeof ResourceAccessTable>,
  | "principal"
  | "directGrants"
  | "inheritedGrants"
  | "resolveScopeName"
  | "personalSpaceId"
  | "canManage"
  | "stagedGrantsRef"
  | "onOpenResource"
  | "hideEmpty"
> & {
  active: boolean;
  customRoles: readonly QueryRowType<ReturnType<typeof queries.allRolesLite>>[];
};

export function ScopeAccessEditor({ active, ...props }: Props) {
  const [visited, setVisited] = useState(active);
  if (active && !visited) setVisited(true);
  return visited ? <LoadedScopeAccessEditor {...props} /> : null;
}

function LoadedScopeAccessEditor({
  customRoles,
  ...props
}: Omit<Props, "active">) {
  const [allSpaces, spacesResult] = useQuery(
    queries.spaces({ settingsView: true })
  );
  const [apps, appsResult] = useQuery(queries.connectorsIndex());
  const [allSkills, skillsResult] = useQuery(queries.accessGraphSkills());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const z = useZero();
  const currentUserId = useCurrentUserId();
  const isSelf = props.principal.userId === currentUserId;
  const { resolveScopeName } = props;
  const canReadSpaces = useHasAccess("tenant:spaces_read");
  const canReadApps = useHasAccess("tenant:connected_apps_read");
  const canReadSkills = useHasAccess("tenant:skills_read");
  const { guard: guardSpaceAccess, modal: grantAccessModal } =
    useGrantSpaceAccess();
  const { userGroupIds: userGroupMemberships } = useGroupMemberships();
  const [confirmingSelfRemoveAccess, setConfirmingSelfRemoveAccess] = useState<{
    scopeId: string;
    scopeKind: ShareableScopeKind;
    desired: GrantEntry[];
  } | null>(null);
  const [pendingConnectorRemoval, setPendingConnectorRemoval] = useState<{
    appId: string;
    desired: GrantEntry[];
  } | null>(null);
  const [myGrants] = useCurrentUserGrants();
  const manageableSpaceIds = getWritableScopeIds(
    myGrants,
    allSpaces.map((s) => s.id),
    SCOPE_MANAGE_GRANTS_PERMISSION.space
  );
  const manageableAppIds = getWritableScopeIds(
    myGrants,
    apps.map((a) => a.id),
    SCOPE_MANAGE_GRANTS_PERMISSION.connected_app
  );
  const manageableSkillIds = getWritableScopeIds(
    myGrants,
    allSkills.map((s) => s.id),
    SCOPE_MANAGE_GRANTS_PERMISSION.skill
  );
  const currentGrantsFor = (scopeId: string, scopeKind: ShareableScopeKind) => {
    if (scopeKind === "connected_app")
      return apps.find((a) => a.id === scopeId)?.roleGrantsAsScope;
    if (scopeKind === "skill")
      return allSkills.find((s) => s.id === scopeId)?.roleGrantsAsScope;
    return allSpaces.find((s) => s.id === scopeId)?.roleGrantsAsScope;
  };
  const commitScope = (
    scopeId: string,
    scopeKind: ShareableScopeKind,
    desired: GrantEntry[],
    onCommitted?: () => void,
    onBlocked?: () => void
  ) => {
    setError(null);
    // Space grants also require access to the connectors and skills used there.
    const doMutate = () => {
      const { add, remove } = deriveScopeGrantDelta(
        currentGrantsFor(scopeId, scopeKind) ?? [],
        desired
      );
      z.mutate(
        sharedMutators.patchScopeGrants({
          scopeId,
          scopeKind,
          add,
          remove,
          autoGrantConnectors: scopeKind === "space",
          autoGrantSkills: scopeKind === "space",
        })
      );
    };
    const space =
      scopeKind === "space"
        ? allSpaces.find((s) => s.id === scopeId)
        : undefined;
    if (!space) {
      doMutate();
      return;
    }
    const commitSpace = async () => {
      setBusy(true);
      setError(null);
      try {
        // Load complete usage for this space only, including its Git connector.
        const [usage, gitRemote] = await Promise.all([
          z.run(queries.connectorUsagesBySpace({ spaceIds: [scopeId] }), {
            type: "complete",
          }),
          z.run(queries.loadSpaceGitRemote({ spaceId: scopeId }), {
            type: "complete",
          }),
        ]);
        if (!mounted.current) return false;
        const connectorIds = connectorIdsBySpace(usage).get(scopeId);
        const gitConnectorId = gitRemote?.appUsage?.connectedAppId;
        const spaceGrants = space.roleGrantsAsScope;
        const gaps = computeSpaceMemberSupplierGap({
          currentSpaceGrants: spaceGrants,
          desiredSpaceGrants: grantEntriesToRows(desired, customRoles),
          connectors: apps.filter(
            (a) => connectorIds?.has(a.id) || a.id === gitConnectorId
          ),
          skills: usedSkillsForSpace(spaceGrants, allSkills),
          memberships: userGroupMemberships,
          manageableConnectorIds: manageableAppIds,
          manageableSkillIds,
        });
        setBusy(false);
        if (gaps.length === 0) {
          doMutate();
          return;
        }
        guardSpaceAccess({
          spaceName: space.name,
          gaps,
          proceed: () => {
            doMutate();
            onCommitted?.();
          },
          onBlocked,
        });
      } catch {
        if (mounted.current) {
          setBusy(false);
          setError("Couldn’t check this space’s access. Try again.");
        }
      }
      return false;
    };
    return commitSpace();
  };
  const requestSelfRemoveAccess = (
    scopeId: string,
    scopeKind: ShareableScopeKind,
    desired: GrantEntry[]
  ) => {
    setConfirmingSelfRemoveAccess({ scopeId, scopeKind, desired });
  };
  const removeConnectorAccess = (appId: string, desired: GrantEntry[]) => {
    setPendingConnectorRemoval({ appId, desired });
  };
  const computeConnectorImpact = useConnectorDisconnectImpact(
    pendingConnectorRemoval?.appId ?? null
  );
  const pendingConnectorApp = pendingConnectorRemoval
    ? apps.find((a) => a.id === pendingConnectorRemoval.appId)
    : null;
  const connectorRemovalImpact =
    pendingConnectorRemoval && pendingConnectorApp
      ? computeConnectorImpact(
          pendingConnectorApp.roleGrantsAsScope,
          pendingConnectorRemoval.desired
        )
      : null;
  const connectorChatCount = useAffectedChatCount(
    pendingConnectorRemoval?.appId ?? null,
    connectorRemovalImpact?.affectedUserIds ?? []
  );

  return (
    <>
      {error && (
        <div role="alert">
          <Text color="var(--text-error)">{error}</Text>
        </div>
      )}
      <ResourceAccessTable
        busy={
          busy ||
          [spacesResult, appsResult, skillsResult].some(
            (r) => r.type !== "complete"
          )
        }
        {...props}
        spaces={allSpaces}
        skills={allSkills}
        apps={apps}
        customRoles={customRoles}
        isSelf={isSelf}
        manageableSpaceIds={manageableSpaceIds}
        manageableAppIds={manageableAppIds}
        manageableSkillIds={manageableSkillIds}
        hasTenantRead={{
          space: canReadSpaces,
          connected_app: canReadApps,
          skill: canReadSkills,
        }}
        commitScope={commitScope}
        onRequestSelfRemove={requestSelfRemoveAccess}
        onRemoveConnectorAccess={removeConnectorAccess}
      />
      {grantAccessModal}
      <ConfirmModal
        open={confirmingSelfRemoveAccess !== null}
        onClose={() => setConfirmingSelfRemoveAccess(null)}
        onConfirm={() => {
          if (confirmingSelfRemoveAccess) {
            commitScope(
              confirmingSelfRemoveAccess.scopeId,
              confirmingSelfRemoveAccess.scopeKind,
              confirmingSelfRemoveAccess.desired
            );
          }
          setConfirmingSelfRemoveAccess(null);
        }}
        title="Remove your own access?"
        description={
          confirmingSelfRemoveAccess
            ? `You’ll lose your direct access to ${
                resolveScopeName(
                  confirmingSelfRemoveAccess.scopeId,
                  confirmingSelfRemoveAccess.scopeKind
                ) ?? "this resource"
              }.`
            : ""
        }
        confirmLabel="Remove"
        destructive
      />

      <ConfirmModal
        open={pendingConnectorRemoval !== null}
        onClose={() => setPendingConnectorRemoval(null)}
        onConfirm={() => {
          if (pendingConnectorRemoval)
            commitScope(
              pendingConnectorRemoval.appId,
              "connected_app",
              pendingConnectorRemoval.desired
            );
          setPendingConnectorRemoval(null);
        }}
        title={isSelf ? "Remove your own access?" : "Remove access?"}
        description={
          connectorRemovalImpact &&
          connectorRemovalImpact.workflows.length > 0 ? (
            <>
              Removing this access means{" "}
              <Count
                n={connectorRemovalImpact.workflows.length}
                noun="workflow"
              />{" "}
              will need a connector replaced.
            </>
          ) : isSelf ? (
            `You’ll lose your direct access to ${pendingConnectorApp?.name ?? "this connector"}.`
          ) : (
            `Removes direct access to ${pendingConnectorApp?.name ?? "this connector"}.`
          )
        }
        confirmLabel="Remove"
        destructive
      >
        {connectorRemovalImpact && (
          <DisconnectWarning
            {...connectorRemovalImpact}
            chatCount={connectorChatCount}
          />
        )}
      </ConfirmModal>
    </>
  );
}
